import React, { createContext, useContext, useState, useRef, useCallback, useMemo, useEffect } from 'react';
import Toast from '../components/Toast';

const ToastContext = createContext(null);

const TOAST_DURATION_MS = 3000;

/**
 * App-wide single-slot toast, replacing the old native-dialog share
 * fallbacks. `showToast(message)` replaces whatever message is currently
 * shown and (re)starts a 3s auto-dismiss timer — there is only ever one
 * message on screen at a time. Rendered by the provider itself at the end
 * of its tree (see Toast.js) so no consumer has to mount anything to see
 * toasts appear.
 */
export function ToastProvider({ children }) {
  const [message, setMessage] = useState(null);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // StrictMode-safe: explicitly (re)set in the effect body rather than
  // relying only on the initial useRef(null) value — a dev-mode double
  // invoke (setup -> cleanup -> setup) of this mount-only effect must still
  // leave timerRef null going into real use. Cleanup always clears whatever
  // timer is live when the provider actually unmounts.
  useEffect(() => {
    timerRef.current = null;
    return clearTimer;
  }, [clearTimer]);

  const showToast = useCallback((msg) => {
    clearTimer();
    setMessage(msg);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setMessage(null);
    }, TOAST_DURATION_MS);
  }, [clearTimer]);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toast message={message} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
